// useMatrixSourcePreview — fetch + cache a STORED matrix evidence source's preview
// ({label, summary, open} + optional discovery nav target) for the click-to-peek
// popover, keyed on an evidence_id or OPAQUE source_link_id. The matrix twin of
// useSourcePreview (which keys on a raw source id) — a stored matrix source carries
// only the opaque link id, resolved server-side via /claims-matrix/source-preview. Module-level session
// cache + key-owned derive so a request change can never surface a stale peek; and every
// open REVALIDATES against the server read-time gate (the cache is paint-only), so a
// source that became un-citable mid-session can't be re-served from cache.
//
// Accepted limitation (paired ship-gate, dispositioned LOW): on a re-peek of a source
// that became un-citable since the last peek, the cache-seeded initial paint shows the
// prior (already-seen) summary for the ~1 fetch round-trip until the revalidation 404
// resolves and evicts it. This is cosmetic, not a disclosure: the flashed summary is
// content this client already held, and Open re-authorizes server-side regardless of
// this gate (a doc mints a fresh access code the doc-viewer Lambda RLS-gates; discovery
// navigates to the RLS-gated workspace — see useOpenSource), so the flash can surface
// nothing the backend won't independently authorize. Killing the flash would mean a
// loading spinner on EVERY peek — not worth it for a sub-second repaint of held content.
import { useEffect, useState } from 'react';
import {
  fetchMatrixSourcePreview,
  type MatrixSourcePreview,
  type MatrixSourcePreviewKey,
} from '../lib/database';

const _cache = new Map<string, MatrixSourcePreview>();

function cacheKey(caseId: string, key: MatrixSourcePreviewKey): string {
  if ('evidence_id' in key) return `${caseId}:evidence:${key.evidence_id}`;
  return `${caseId}:link:${key.source_link_id}`;
}

/** Clear the in-memory matrix-preview cache (e.g. on sign-out). */
export function clearMatrixSourcePreviewCache(): void {
  _cache.clear();
}

export interface UseMatrixSourcePreviewResult {
  preview: MatrixSourcePreview | null;
  loading: boolean;
  error: string | null;
}

/**
 * Resolve a matrix source preview from an evidence or opaque link key. The cache seeds an
 * instant initial paint, but every open re-fetches to revalidate against the server
 * read-time gate, so a now-un-citable source fails closed (preview dropped, error shown).
 * @param caseId       the current case
 * @param key          the evidence id or opaque open_target.source_link_id, or null
 * @param enabled      gate the fetch (e.g. only once the popover is open)
 */
export function useMatrixSourcePreview(
  caseId: string,
  key: MatrixSourcePreviewKey | null,
  enabled: boolean,
): UseMatrixSourcePreviewResult {
  const reqKey = key ? cacheKey(caseId, key) : null;

  // State tagged with the key it was fetched for; everything returned is DERIVED
  // against the CURRENT reqKey at render time, so a request change (incl. to null)
  // can never surface a stale summary or keep a stale Open target live.
  const [loaded, setLoaded] = useState<{ key: string; value: MatrixSourcePreview } | null>(
    () => {
      if (!reqKey) return null;
      const cached = _cache.get(reqKey);
      return cached ? { key: reqKey, value: cached } : null;
    },
  );
  const [error, setError] = useState<{ key: string; message: string } | null>(null);

  useEffect(() => {
    if (!enabled || !key || !reqKey) return;
    let active = true;
    // Always REVALIDATE against the server read-time gate on open, even on a cache hit.
    // A stored source can become un-citable mid-session — a document later flagged
    // privileged / pulled from production, or a discovery response later un-served — and
    // the backend now 404s it (handle_matrix_source_preview's read-time gate). Serving the
    // stale cached preview would bypass that gate, so we re-fetch every open and, on ANY
    // failure, EVICT the cache + drop the rendered preview + surface the error (fail-closed)
    // — a momentarily-stale within-session re-peek can never outlive the gate, and the Open
    // affordance (canOpenSource(preview)) goes dead with it. The cache still seeds the
    // initial paint above for an instant (then server-confirmed) render.
    setError(null);
    fetchMatrixSourcePreview(caseId, key)
      .then((p) => {
        if (!active) return;
        _cache.set(reqKey, p);
        setLoaded({ key: reqKey, value: p });
      })
      .catch((e: unknown) => {
        if (!active) return;
        _cache.delete(reqKey);
        setLoaded((prev) => (prev && prev.key === reqKey ? null : prev));
        setError({ key: reqKey, message: e instanceof Error ? e.message : 'Could not load this source.' });
      });
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reqKey]);

  const cachedNow = reqKey ? _cache.get(reqKey) ?? null : null;
  const preview = reqKey ? (loaded && loaded.key === reqKey ? loaded.value : cachedNow) : null;
  const errorMsg = reqKey && error && error.key === reqKey ? error.message : null;
  const loading = !!reqKey && enabled && preview == null && errorMsg == null;

  return { preview, loading, error: errorMsg };
}
